import React, { useRef, useState } from 'react';

import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import ChartTooltip from './ChartTooltip';
import ChartValueLabel from './ChartValueLabel';
import DepartureStatus from './DepartureStatus';
import EmptyPrediction from './EmptyPrediction';
import { emptyPredictionText } from './emptyPredictionText';
import {
  DeparturePoint,
  agg,
  flagLabels,
  marginDomain,
  money,
  moneyShort,
  pct,
  seatsLeft,
  travellerCount,
} from './logic';
import { tcc } from './palette';
import { CostBasis } from './types';
import useValueLabelLayout from './useValueLabelLayout';

const actionLines = (label: string): [string, string] => {
  const [first, ...rest] = label.split(' ');
  return [first, rest.join(' ')];
};
const number = (value: number | null) => (value == null ? '—' : Math.round(value).toLocaleString('en-AU'));
function DepartureTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DeparturePoint }> }) {
  const p = payload?.[0]?.payload;
  if (!active || !p) {
    return null;
  }
  const d = p.departure;
  const rows = [
    [
      'Travellers / Max',
      `${number(p.pax)} / ${number(p.capacity)}`,
      emptyPredictionText(d) ? '—' : `${number(p.ppax)} / ${number(p.capacity)}`,
    ],
    ['Seats Left', number(seatsLeft(p.capacity, p.pax)), number(seatsLeft(p.capacity, p.ppax))],
    ['Load Factor', pct(p.lf), pct(p.plf)],
    ['TTV', money(p.ttv), money(p.pttv)],
    ['Cost', money(p.cost), money(p.pcost)],
    ['Margin', money(p.m), money(p.pmv)],
    ['Margin Rate', pct(p.mp), pct(p.pm)],
  ];
  return (
    <ChartTooltip>
      <Paper variant="outlined" sx={{ p: 1.5, maxWidth: 360 }}>
        <Typography variant="subtitle2">
          {p.label} · {d.dep_name}
        </Typography>
        <Typography variant="caption" display="block">
          {d.state} · Handback {d.days_to_handback < 0 ? 'passed' : `in ${d.days_to_handback} days`}
        </Typography>
        <Chip
          size="small"
          label={flagLabels[p.flag]}
          sx={{
            my: 0.5,
            color: p.flag === 'red' ? tcc.redText : p.flag === 'amber' ? tcc.amberText : 'text.secondary',
          }}
        />
        <Table size="small" sx={{ '& td, & th': { border: 0, p: 0.35, fontSize: 12, bgcolor: 'transparent' } }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={{ color: tcc.bookedText }}>Current</TableCell>
              <TableCell sx={{ color: tcc.predictedText }}>Predicted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([label, booked, predicted]) => (
              <TableRow key={String(label)}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">{booked}</TableCell>
                <TableCell align="right">{predicted}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" display="block">
          Break-Even: {travellerCount(p.breakeven)} · {pct(p.beLf)} LF
        </Typography>
        <EmptyPrediction departure={d} />
        <Typography variant="caption" display="block" color="text.secondary">
          {d.pred_pax_source === 'zero_model'
            ? 'Indicative'
            : d.pred_pax_source === 'model'
              ? `model, range P10–P90: ${number(d.pred_pax_p10)}–${number(d.pred_pax_p90)}`
              : 'pace'}
          {p.isFinal ? ' · final' : ''}
        </Typography>
        <Typography variant="caption" display="block">
          Margin = TTV minus supplier cost, minus ICS at cost-plus where the tour has one
        </Typography>
      </Paper>
    </ChartTooltip>
  );
}

function DepartureMark({ cx, cy, width, color }: { cx?: number; cy?: number; width: number; color: string }) {
  if (cx == null || cy == null || !Number.isFinite(cy)) {
    return null;
  }
  return (
    <line
      x1={cx - width / 2}
      x2={cx + width / 2}
      y1={cy}
      y2={cy}
      stroke={color}
      strokeWidth={2}
      strokeDasharray="4 3"
    />
  );
}

const tableHeaders = [
  'Departure Date',
  'Status',
  'Load Factor',
  'Predicted Load Factor',
  'Current Booked Numbers: TTV',
  'Predicted at Handback: TTV',
  'Break-Even Group Size',
];

/** Departure travellers and margin share the same selection, tooltip and table totals. */
export default function DepartureCharts({
  points,
  basis,
  asTable,
  selected,
  onSelect,
}: {
  points: Array<DeparturePoint>;
  basis: CostBasis;
  asTable: boolean;
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const chartRoot = useRef<HTMLDivElement>(null);
  useValueLabelLayout(chartRoot);
  const theme = useTheme();
  const [chartWidth, setChartWidth] = useState(560);
  const mobile = useMediaQuery('(max-width: 390px)');
  const plotWidth = mobile ? Math.max(chartWidth, points.length * 38 + 112) : chartWidth;
  const dateSlot = (plotWidth - 64) / Math.max(1, points.length);
  // Prefer wrapping inside a single date slot. Stagger only when a word cannot fit.
  const longestLine = Math.max(
    ...points.map((p) => Math.max(...actionLines(flagLabels[p.flag]).map((line) => line.length))),
  );
  const minimumWrappedWidth = Math.max(55, longestLine * 6 + 4);
  const singleLineWidth = Math.max(
    ...points.map((p) => Math.max(p.label.length * 5.5, flagLabels[p.flag].length * 5.5 + 4)),
  );
  const wrap = dateSlot - 4 < singleLineWidth;
  const stagger = dateSlot - 4 < minimumWrappedWidth;
  const slotWidth = Math.min(104, dateSlot * (stagger ? 2 : 1) - 4);
  const chartHeight = stagger ? 360 : wrap ? 320 : 300;
  const total = agg(
    points.map((p) => p.departure),
    basis,
  );
  const known = points.filter((p) => p.breakeven != null && p.capacity > 0);
  const totalBreakeven = known.reduce((s, p) => s + (p.breakeven ?? 0), 0);
  const chartData = points;
  const capacity = Math.max(1, ...points.map((p) => Math.max(p.cap, p.pax + p.extra, p.breakeven ?? 0)));
  const opacity = (p: DeparturePoint) => (selected && p.key !== selected ? 0.35 : p.isFinal ? 0.6 : 1);
  const onPoint = (p: DeparturePoint) => onSelect(p.key === selected ? null : p.key);
  const markWidth = Math.max(8, Math.min(32, 520 / Math.max(points.length, 1) - 8));
  const roomy = useMediaQuery(theme.breakpoints.up('md'));
  const labels = roomy && points.length <= 9;
  const grid = theme.palette.mode === 'dark' ? tcc.grid.dark : tcc.grid.light;
  const tooltip = (
    <Tooltip
      content={<DepartureTooltip />}
      offset={0}
      allowEscapeViewBox={{ x: true, y: true }}
      wrapperStyle={{ zIndex: 10, pointerEvents: 'none', transform: 'none', top: 0, left: 0 }}
      cursor={{ fill: 'rgba(28, 92, 171, 0.06)' }}
    />
  );
  const xAxis = (
    <XAxis
      dataKey="key"
      padding={{ left: 0, right: 0 }}
      tickLine={false}
      height={stagger ? 140 : wrap ? 96 : 84}
      interval={0}
      tick={({ x, y, payload }) => {
        const p = points.find((point) => point.key === payload.value);
        if (!p) {
          return <g />;
        }
        const index = points.indexOf(p);
        const rowY = Number(y) + (stagger && index % 2 ? 62 : 0);
        return (
          <g
            transform={`translate(${x},${rowY})`}
            role="button"
            tabIndex={0}
            aria-label={`Show departure ${p.label}`}
            onClick={(event) => {
              event.stopPropagation();
              onPoint(p);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onPoint(p);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {stagger && index % 2 === 1 && (
              <line data-departure-tick x1={0} x2={0} y1={-62} y2={0} stroke={tcc.axis} strokeWidth={0.5} />
            )}
            <text data-departure-date={p.key} dy={12} textAnchor="middle" fill={tcc.axis} fontSize={11}>
              {wrap ? (
                <>
                  <tspan x={0}>{p.label.slice(0, -3)}</tspan>
                  <tspan x={0} dy={13}>
                    {p.label.slice(-2)}
                  </tspan>
                </>
              ) : (
                p.label
              )}
            </text>
            <foreignObject
              x={-Math.min(slotWidth, 2 * (Number(x) - 4), 2 * (plotWidth - Number(x) - 4)) / 2}
              y={wrap ? 30 : 20}
              width={Math.min(slotWidth, 2 * (Number(x) - 4), 2 * (plotWidth - Number(x) - 4))}
              height={34}
            >
              <Box
                data-chart-action-pill
                data-departure-pill={p.key}
                sx={{
                  fontSize: 11,
                  lineHeight: '13px',
                  textAlign: 'center',
                  borderRadius: 2,
                  px: 0,
                  py: 0.25,
                  bgcolor:
                    p.flag === 'red'
                      ? 'rgba(208,59,59,.14)'
                      : p.flag === 'amber'
                        ? 'rgba(250,178,25,.18)'
                        : p.flag === 'green'
                          ? 'rgba(99,127,121,.14)'
                          : p.flag === 'empty'
                            ? 'rgba(99,87,121,.14)'
                            : 'rgba(123,132,144,.14)',
                  color:
                    p.flag === 'red'
                      ? tcc.redText
                      : p.flag === 'amber'
                        ? tcc.amberText
                        : p.flag === 'green'
                          ? tcc.greenText
                          : 'text.secondary',
                }}
              >
                {wrap
                  ? actionLines(flagLabels[p.flag]).map((line, index) => (
                      <Box component="span" key={index} sx={{ display: 'block', height: '13px', whiteSpace: 'nowrap' }}>
                        {line}
                      </Box>
                    ))
                  : flagLabels[p.flag]}
              </Box>
            </foreignObject>
          </g>
        );
      }}
    />
  );
  const click = (state: unknown) => {
    const s = state as { activeIndex?: number | string; activeTooltipIndex?: number | string };
    const index = Number(s?.activeIndex ?? s?.activeTooltipIndex);
    if (points[index]) {
      onPoint(points[index]);
    }
  };
  if (!points.length) {
    return <Typography sx={{ py: 4 }}>No counted departures in this date range</Typography>;
  }
  if (asTable) {
    return (
      <Box sx={{ overflowX: 'auto', mt: 2 }}>
        <Table size="small" aria-label="Booked and predicted by departure">
          <TableHead>
            <TableRow>
              {tableHeaders.map((label) => (
                <TableCell key={label} sx={{ whiteSpace: 'normal', fontWeight: 600 }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ '& td': { whiteSpace: 'normal', fontVariantNumeric: 'tabular-nums' } }}>
            {points.map((p) => (
              <TableRow
                key={p.key}
                hover
                selected={p.key === selected}
                onClick={() => onPoint(p)}
                sx={{ cursor: 'pointer', opacity: p.isFinal ? 0.6 : 1 }}
              >
                <TableCell>
                  {p.label}
                  <Typography variant="caption" display="block">
                    {p.departure.dep_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <DepartureStatus departure={p.departure} />
                </TableCell>
                <TableCell>
                  {pct(p.lf)}
                  <Typography variant="caption" display="block">
                    {number(p.pax)} of {number(p.capacity)} seats
                  </Typography>
                </TableCell>
                <TableCell>
                  {emptyPredictionText(p.departure) ? (
                    <EmptyPrediction departure={p.departure} />
                  ) : (
                    <>
                      {pct(p.plf)}
                      <Typography variant="caption" display="block">
                        {number(p.ppax)} of {number(p.capacity)} seats
                      </Typography>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  TTV {money(p.ttv)}
                  <Typography variant="caption" display="block">
                    Margin {money(p.m)} · {pct(p.mp)}
                  </Typography>
                </TableCell>
                <TableCell>
                  TTV {money(p.pttv)}
                  <Typography variant="caption" display="block">
                    Margin {money(p.pmv)} · {pct(p.pm)}
                  </Typography>
                </TableCell>
                <TableCell>{number(p.breakeven)}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ '& td': { fontWeight: 700 } }}>
              <TableCell>Total</TableCell>
              <TableCell>{points.length}</TableCell>
              <TableCell>{pct(total.lf)}</TableCell>
              <TableCell>{pct(total.plf)}</TableCell>
              <TableCell>{money(total.ttv)}</TableCell>
              <TableCell>{money(total.pttv)}</TableCell>
              <TableCell>{totalBreakeven || '—'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  }
  return (
    <Box
      ref={chartRoot}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: points.length > 14 ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
        },
        gap: 1,
        mt: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2">Travellers by Departure Date</Typography>
        <Typography variant="caption" color="text.secondary">
          Current travellers and predicted travellers at handback
        </Typography>
        <Box
          sx={{
            mt: 2,
            height: chartHeight,
            overflowX: mobile ? 'auto' : 'visible',
            '& *:focus:not(:focus-visible)': { outline: 'none' },
          }}
          data-testid="command-centre-departure-travellers-chart"
          data-departure-count={points.length}
        >
          <ResponsiveContainer
            onResize={(width) => setChartWidth(width)}
            width={mobile ? plotWidth : '100%'}
            height={chartHeight}
            minWidth={1}
            initialDimension={{ width: 560, height: 300 }}
          >
            <ComposedChart
              data={chartData}
              onClick={click}
              margin={{ top: 32, right: 12, left: 0, bottom: 8 }}
              barCategoryGap="25%"
            >
              <CartesianGrid vertical={false} stroke={grid} />
              {xAxis}
              <YAxis
                domain={[0, Math.ceil(capacity * 1.1)]}
                width={38}
                allowDecimals={false}
                tick={{ fill: tcc.axis, fontSize: 11 }}
              />
              {tooltip}
              <Bar
                dataKey="pax"
                fill={tcc.booked}
                stroke={theme.palette.mode === 'dark' ? '#c9d7e5' : undefined}
                strokeWidth={1.5}
                name="Current Booked Numbers"
                isAnimationActive={false}
              >
                {points.map((p) => (
                  <Cell key={p.key} fillOpacity={opacity(p)} />
                ))}
                {labels && <LabelList dataKey="lf" content={<ChartValueLabel format="ratio" />} />}
              </Bar>
              <Bar
                dataKey="ppax"
                fill={tcc.predicted}
                stroke={theme.palette.mode === 'dark' ? '#88b8e4' : undefined}
                name="Predicted at Handback"
                isAnimationActive={false}
              >
                {points.map((p) => (
                  <Cell key={p.key} fillOpacity={opacity(p)} />
                ))}
                {labels && <LabelList dataKey="plf" content={<ChartValueLabel kind="predicted" format="ratio" />} />}
              </Bar>
              <Scatter
                dataKey="capacity"
                name="Max Capacity"
                isAnimationActive={false}
                shape={(props: { cx?: number; cy?: number }) => (
                  <DepartureMark {...props} width={markWidth} color={tcc.axis} />
                )}
              />
              <Scatter
                dataKey="breakeven"
                name="Break-Even"
                isAnimationActive={false}
                shape={(props: { cx?: number; cy?: number }) => (
                  <DepartureMark {...props} width={markWidth} color={tcc.breakeven} />
                )}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        {points.length > 20 && (
          <Typography variant="caption" color="text.secondary">
            {points[0].label} – {points[points.length - 1].label} · Departures: {points.length}
          </Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2">Margin by Departure Date</Typography>
        <Typography variant="caption" color="text.secondary">
          Margin after supplier cost and ICS at cost-plus (ICS tours only)
        </Typography>
        <Box
          sx={{
            mt: 2,
            height: chartHeight,
            overflowX: mobile ? 'auto' : 'visible',
            '& *:focus:not(:focus-visible)': { outline: 'none' },
          }}
          data-testid="command-centre-departure-margin-chart"
          data-departure-count={points.length}
        >
          <ResponsiveContainer
            onResize={(width) => setChartWidth(width)}
            width={mobile ? plotWidth : '100%'}
            height={chartHeight}
            minWidth={1}
            initialDimension={{ width: 560, height: 300 }}
          >
            <ComposedChart data={chartData} onClick={click} margin={{ top: 32, right: 12, left: 0, bottom: 8 }}>
              <CartesianGrid vertical={false} stroke={grid} />
              {xAxis}
              <YAxis
                domain={marginDomain(points.flatMap((p) => [p.m, p.pmv]))}
                width={52}
                tickFormatter={(v) => moneyShort(v)}
                tick={{ fill: tcc.axis, fontSize: 11 }}
              />
              <ReferenceLine y={0} stroke={tcc.axis} />
              {tooltip}
              <Bar
                dataKey="m"
                name="Current Booked Numbers"
                fill={tcc.booked}
                stroke={theme.palette.mode === 'dark' ? '#c9d7e5' : undefined}
                strokeWidth={1.5}
                isAnimationActive={false}
              >
                {points.map((p) => (
                  <Cell key={p.key} fillOpacity={opacity(p)} />
                ))}
              </Bar>
              <Bar
                dataKey="pmv"
                name="Predicted at Handback"
                fill={tcc.predicted}
                stroke={theme.palette.mode === 'dark' ? '#88b8e4' : undefined}
                isAnimationActive={false}
              >
                {points.map((p) => (
                  <Cell key={p.key} fillOpacity={opacity(p)} />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
}
